import React from 'react';

const TermsOfService = () => {
  const styles = {
    container: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: 1.6,
      color: '#333',
      backgroundColor: '#f9fafb',
      minHeight: '100vh',
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem',
      background: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderRadius: '8px',
      marginTop: '2rem',
      marginBottom: '2rem',
    },
    backLink: {
      display: 'inline-block',
      marginBottom: '2rem',
      color: '#1e40af',
      textDecoration: 'none',
      fontWeight: 500,
    },
    backLinkHover: {
      textDecoration: 'underline',
    },
    title: {
      color: '#1e40af',
      fontSize: '2.5rem',
      marginBottom: '1rem',
      textAlign: 'center',
    },
    lastUpdated: {
      textAlign: 'center',
      fontStyle: 'italic',
      color: '#6b7280',
      marginBottom: '2rem',
      padding: '1rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '4px',
    },
    heading: {
      color: '#1e40af',
      fontSize: '1.5rem',
      marginTop: '2rem',
      marginBottom: '1rem',
      borderBottom: '2px solid #e5e7eb',
      paddingBottom: '0.5rem',
    },
    subheading: {
      color: '#374151',
      fontSize: '1.25rem',
      marginTop: '1.5rem',
      marginBottom: '0.75rem',
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#4b5563',
    },
    list: {
      marginLeft: '2rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#4b5563',
    },
    highlight: {
      backgroundColor: '#fef3c7',
      padding: '1rem',
      borderRadius: '4px',
      margin: '1rem 0',
      borderLeft: '4px solid #f59e0b',
    },
    importantNotice: {
      backgroundColor: '#fee2e2',
      padding: '1rem',
      borderRadius: '4px',
      margin: '1rem 0',
      borderLeft: '4px solid #ef4444',
    },
    contactInfo: {
      backgroundColor: '#eff6ff',
      padding: '1.5rem',
      borderRadius: '8px',
      marginTop: '2rem',
      borderLeft: '4px solid #1e40af',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <a href="/" style={styles.backLink}>← Back to PersonalityAI</a>
        <h1 style={styles.title}>PersonalityAI Terms of Service</h1>
        <div style={styles.lastUpdated}>
          <strong>Last Updated:</strong> July 07, 2025<br />
          <strong>Effective Date:</strong> July 07, 2025
        </div>
        <h2 style={styles.heading}>1. Agreement to Terms</h2>
        <p style={styles.paragraph}>By accessing or using PersonalityAI ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.</p>
        <h2 style={styles.heading}>2. Description of Service</h2>
        <p style={styles.paragraph}>PersonalityAI is a browser extension and web application that provides:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>AI prompt enhancement based on personality profiles</li>
          <li style={styles.listItem}>Cross-platform compatibility with various AI services</li>
          <li style={styles.listItem}>Behavioral analytics and usage insights</li>
          <li style={styles.listItem}>Revenue sharing opportunities for qualifying users</li>
        </ul>
        <h2 style={styles.heading}>3. User Accounts</h2>
        <h3 style={styles.subheading}>3.1 Account Creation</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>You must provide accurate, complete information</li>
          <li style={styles.listItem}>You are responsible for maintaining account security</li>
          <li style={styles.listItem}>You must be at least 13 years old to create an account</li>
          <li style={styles.listItem}>One account per person; multiple accounts are prohibited</li>
        </ul>
        <h3 style={styles.subheading}>3.2 Account Responsibilities</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Maintain confidentiality of login credentials</li>
          <li style={styles.listItem}>Notify us immediately of unauthorized access</li>
          <li style={styles.listItem}>Accept responsibility for all activities under your account</li>
          <li style={styles.listItem}>Comply with all applicable laws and regulations</li>
        </ul>
        <h2 style={styles.heading}>4. Subscription Services</h2>
        <h3 style={styles.subheading}>4.1 Subscription Plans</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>Free Plan</strong>: Limited daily AI prompt enhancements</li>
          <li style={styles.listItem}><strong>Pro Plan</strong>: Unlimited enhancements and advanced features</li>
          <li style={styles.listItem}><strong>Enterprise Plan</strong>: Team features and custom integrations</li>
        </ul>
        <h3 style={styles.subheading}>4.2 Billing and Payment</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Subscription fees are billed in advance</li>
          <li style={styles.listItem}>Payment processing handled by Stripe</li>
          <li style={styles.listItem}>Automatic renewal unless cancelled</li>
          <li style={styles.listItem}>No refunds for partial usage periods</li>
        </ul>
        <h3 style={styles.subheading}>4.3 Price Changes</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>We may change subscription prices with 30 days notice</li>
          <li style={styles.listItem}>Existing subscribers locked in at current rate until renewal</li>
          <li style={styles.listItem}>Price changes apply to new subscriptions immediately</li>
        </ul>
        <h2 style={styles.heading}>5. Revenue Sharing Program</h2>
        <div style={styles.highlight}>
          <h3 style={styles.subheading}>5.1 Eligibility</h3>
          <p style={styles.paragraph}>Participation in our revenue sharing program requires:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Active Pro or Enterprise subscription</li>
            <li style={styles.listItem}>Consistent usage meeting quality thresholds</li>
            <li style={styles.listItem}>Compliance with all Terms of Service</li>
            <li style={styles.listItem}>Verification of identity and tax information</li>
          </ul>
        </div>
        <h3 style={styles.subheading}>5.2 Payment Structure</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>Quality-Based Compensation</strong>: Payments determined by algorithmic quality scoring</li>
          <li style={styles.listItem}><strong>Minimum Payout</strong>: $10.00 USD minimum for payment processing</li>
          <li style={styles.listItem}><strong>Payment Schedule</strong>: Monthly payments, processed within 30 days of month-end</li>
          <li style={styles.listItem}><strong>Payment Methods</strong>: Stripe Connect, bank transfer, or other approved methods</li>
        </ul>
        <h3 style={styles.subheading}>5.3 Quality Metrics</h3>
        <p style={styles.paragraph}>Compensation is based on:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Interaction quality and user satisfaction</li>
          <li style={styles.listItem}>Behavioral analytics indicating effective usage</li>
          <li style={styles.listItem}>Platform diversity and engagement levels</li>
          <li style={styles.listItem}>Algorithmic assessment of value contribution</li>
        </ul>
        <div style={styles.importantNotice}>
          <h3 style={styles.subheading}>5.4 Revenue Program Terms</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Discretionary Program</strong>: We reserve the right to modify or terminate the program</li>
            <li style={styles.listItem}><strong>Suspension Rights</strong>: We may suspend payments for policy violations</li>
            <li style={styles.listItem}><strong>Tax Responsibility</strong>: You are responsible for all tax obligations</li>
            <li style={styles.listItem}><strong>No Guaranteed Payments</strong>: Payments are not guaranteed and may vary</li>
          </ul>
        </div>
        <h3 style={styles.subheading}>5.5 Program Modifications</h3>
        <p style={styles.paragraph}>We reserve the right to:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Modify payment structures with 30 days notice</li>
          <li style={styles.listItem}>Pause or suspend the program temporarily or permanently</li>
          <li style={styles.listItem}>Adjust quality metrics and scoring algorithms</li>
          <li style={styles.listItem}>Require additional verification or documentation</li>
        </ul>
        <h2 style={styles.heading}>6. Acceptable Use Policy</h2>
        <h3 style={styles.subheading}>6.1 Prohibited Activities</h3>
        <p style={styles.paragraph}>You may not:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Use the Service for illegal purposes</li>
          <li style={styles.listItem}>Attempt to reverse engineer or hack the Service</li>
          <li style={styles.listItem}>Share account credentials with others</li>
          <li style={styles.listItem}>Use automated tools to interact with the Service</li>
          <li style={styles.listItem}>Interfere with other users' use of the Service</li>
        </ul>
        <h3 style={styles.subheading}>6.2 Content Guidelines</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Do not submit harmful, offensive, or illegal content</li>
          <li style={styles.listItem}>Respect intellectual property rights</li>
          <li style={styles.listItem}>Do not attempt to manipulate quality metrics</li>
          <li style={styles.listItem}>Maintain professional conduct in all interactions</li>
        </ul>
        <h2 style={styles.heading}>7. Data Collection and Privacy</h2>
        <h3 style={styles.subheading}>7.1 Data Collection</h3>
        <p style={styles.paragraph}>We collect and process data as described in our Privacy Policy, including:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Personal information and account data</li>
          <li style={styles.listItem}>Behavioral analytics and usage patterns</li>
          <li style={styles.listItem}>Payment and subscription information</li>
          <li style={styles.listItem}>Performance metrics and quality indicators</li>
        </ul>
        <h3 style={styles.subheading}>7.2 Data Use</h3>
        <p style={styles.paragraph}>Your data is used to:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Provide and improve our services</li>
          <li style={styles.listItem}>Process payments and manage subscriptions</li>
          <li style={styles.listItem}>Generate analytics and insights</li>
          <li style={styles.listItem}>Comply with legal obligations</li>
        </ul>
        <h2 style={styles.heading}>8. Intellectual Property</h2>
        <h3 style={styles.subheading}>8.1 Our Rights</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>PersonalityAI owns all rights to the Service</li>
          <li style={styles.listItem}>Our trademarks, logos, and branding are protected</li>
          <li style={styles.listItem}>The underlying algorithms and technology are proprietary</li>
          <li style={styles.listItem}>User-generated content remains your property</li>
        </ul>
        <h3 style={styles.subheading}>8.2 License to Use</h3>
        <p style={styles.paragraph}>We grant you a limited, non-exclusive license to use the Service for personal or business purposes in accordance with these Terms.</p>
        <h2 style={styles.heading}>9. Third-Party Services</h2>
        <h3 style={styles.subheading}>9.1 AI Platform Integration</h3>
        <p style={styles.paragraph}>Our Service integrates with third-party AI platforms:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>We are not responsible for third-party service availability</li>
          <li style={styles.listItem}>Third-party terms of service apply to your use of their platforms</li>
          <li style={styles.listItem}>We may suspend service if third-party access is restricted</li>
        </ul>
        <h3 style={styles.subheading}>9.2 Payment Processing</h3>
        <p style={styles.paragraph}>Stripe handles payment processing:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Stripe's terms of service apply to payment transactions</li>
          <li style={styles.listItem}>We are not responsible for payment processing failures</li>
          <li style={styles.listItem}>Dispute resolution follows Stripe's procedures</li>
        </ul>
        <h2 style={styles.heading}>10. Service Availability</h2>
        <h3 style={styles.subheading}>10.1 Uptime</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>We strive for 99.9% uptime but do not guarantee availability</li>
          <li style={styles.listItem}>Scheduled maintenance will be announced in advance</li>
          <li style={styles.listItem}>We are not liable for service interruptions</li>
        </ul>
        <h3 style={styles.subheading}>10.2 Modifications</h3>
        <p style={styles.paragraph}>We may:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Modify or discontinue features with reasonable notice</li>
          <li style={styles.listItem}>Update the Service to maintain security and functionality</li>
          <li style={styles.listItem}>Suspend service for maintenance or legal compliance</li>
        </ul>
        <h2 style={styles.heading}>11. Limitation of Liability</h2>
        <h3 style={styles.subheading}>11.1 Disclaimer of Warranties</h3>
        <p style={styles.paragraph}>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.</p>
        <h3 style={styles.subheading}>11.2 Liability Limits</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Our liability is limited to the amount paid by you in the 12 months preceding the claim</li>
          <li style={styles.listItem}>We are not liable for indirect, incidental, or consequential damages</li>
          <li style={styles.listItem}>Some jurisdictions do not allow liability limitations</li>
        </ul>
        <h2 style={styles.heading}>12. Indemnification</h2>
        <p style={styles.paragraph}>You agree to indemnify and hold harmless PersonalityAI from any claims, damages, or expenses arising from:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Your use of the Service</li>
          <li style={styles.listItem}>Your violation of these Terms</li>
          <li style={styles.listItem}>Your violation of any third-party rights</li>
        </ul>
        <h2 style={styles.heading}>13. Termination</h2>
        <h3 style={styles.subheading}>13.1 Termination by You</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>You may terminate your account at any time</li>
          <li style={styles.listItem}>Subscription cancellations take effect at the end of the billing period</li>
          <li style={styles.listItem}>You remain responsible for all charges incurred</li>
        </ul>
        <h3 style={styles.subheading}>13.2 Termination by Us</h3>
        <p style={styles.paragraph}>We may terminate your account for:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Violation of these Terms</li>
          <li style={styles.listItem}>Fraudulent or illegal activity</li>
          <li style={styles.listItem}>Extended periods of inactivity</li>
          <li style={styles.listItem}>Non-payment of fees</li>
        </ul>
        <h3 style={styles.subheading}>13.3 Effect of Termination</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Access to the Service will be discontinued</li>
          <li style={styles.listItem}>Revenue sharing payments will be processed for earned amounts</li>
          <li style={styles.listItem}>Some provisions of these Terms survive termination</li>
        </ul>
        <h2 style={styles.heading}>14. Dispute Resolution</h2>
        <h3 style={styles.subheading}>14.1 Governing Law</h3>
        <p style={styles.paragraph}>These Terms are governed by applicable laws without regard to conflict of law provisions.</p>
        <h3 style={styles.subheading}>14.2 Arbitration</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Disputes will be resolved through binding arbitration</li>
          <li style={styles.listItem}>Arbitration will be conducted under applicable arbitration rules</li>
          <li style={styles.listItem}>You waive the right to participate in class action lawsuits</li>
        </ul>
        <h3 style={styles.subheading}>14.3 Exceptions</h3>
        <p style={styles.paragraph}>The following disputes are not subject to arbitration:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Intellectual property disputes</li>
          <li style={styles.listItem}>Small claims court actions</li>
          <li style={styles.listItem}>Injunctive relief requests</li>
        </ul>
        <h2 style={styles.heading}>15. General Provisions</h2>
        <h3 style={styles.subheading}>15.1 Entire Agreement</h3>
        <p style={styles.paragraph}>These Terms constitute the entire agreement between you and PersonalityAI regarding the Service.</p>
        <h3 style={styles.subheading}>15.2 Severability</h3>
        <p style={styles.paragraph}>If any provision is found unenforceable, the remaining provisions continue in effect.</p>
        <h3 style={styles.subheading}>15.3 Waiver</h3>
        <p style={styles.paragraph}>Our failure to enforce any provision does not constitute a waiver of that provision.</p>
        <h3 style={styles.subheading}>15.4 Assignment</h3>
        <p style={styles.paragraph}>You may not assign these Terms without our written consent. We may assign these Terms at any time.</p>
        <h2 style={styles.heading}>16. Updates to Terms</h2>
        <p style={styles.paragraph}>We may update these Terms from time to time. We will notify you of material changes by:</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>Email notification to registered users</li>
          <li style={styles.listItem}>Prominent notice on our website</li>
          <li style={styles.listItem}>In-app notification in the extension</li>
        </ul>
        <p style={styles.paragraph}>Continued use of the Service after changes constitutes acceptance of the new Terms.</p>
        <h2 style={styles.heading}>17. Contact Information</h2>
        <div style={styles.contactInfo}>
          <p style={styles.paragraph}><strong>PersonalityAI Support Team</strong><br />Email: support@personalityai.com</p>
          <p style={styles.paragraph}><strong>Legal Notices</strong><br />ARACHNEONDISPLAY LLC<br />2253 Park Place Circle<br />Round Rock, Texas 78681<br />United States</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
